/**
 * Hook to detect WebGL support
 * Feature: 011-light-editing
 * Task: T017
 */

import { useState, useEffect } from 'react'

export interface WebGLSupportInfo {
  supported: boolean
  version: number | null // 1 or 2
  error: string | null
}

/**
 * Detects WebGL support in the browser
 * @returns WebGL support information
 */
export function useWebGLSupport(): WebGLSupportInfo {
  const [supportInfo, setSupportInfo] = useState<WebGLSupportInfo>({
    supported: false,
    version: null,
    error: null,
  })

  useEffect(() => {
    try {
      const canvas = document.createElement('canvas')

      // Try WebGL 2 first
      const gl2 = canvas.getContext('webgl2')
      if (gl2) {
        setSupportInfo({
          supported: true,
          version: 2,
          error: null,
        })
        return
      }

      // Fall back to WebGL 1
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
      if (gl) {
        setSupportInfo({
          supported: true,
          version: 1,
          error: null,
        })
        return
      }

      // No WebGL support
      setSupportInfo({
        supported: false,
        version: null,
        error: 'WebGL is not supported in this browser',
      })
    } catch (err) {
      setSupportInfo({
        supported: false,
        version: null,
        error: err instanceof Error ? err.message : 'Unknown error detecting WebGL',
      })
    }
  }, [])

  return supportInfo
}
